/* eslint-disable import/no-cycle */
import {action, decorate, observable} from 'mobx';
import * as moment from 'moment';
import {Api} from '../middleware/api';
import {EntityStore} from './entityStore';
import {InspectionTask} from '../models/inspectionTask';
import {
  updateQualitativeTargetValuesEndpoint, updateQuantitativeTargetValuesEndpoint
} from '../middleware/endpoints/inspectionTask';
import {sortAlphabetically} from '../components/shared/tables/sorters';

export class InspectionTaskStore extends EntityStore {
  inspectionTasks = [];

  constructor(rootStore) {
    super(rootStore, 'inspectionTasks', Api.inspectionTask, InspectionTask, true);
  }

  getDependencies() {
    return [
      {
        store: this.rootStore.inspectionLotStore,
        modelId: 'inspectionLotId',
      },
      {
        store: this.rootStore.inspectionTaskGroupStore,
        modelId: 'inspectionTaskGroupId',
      },
      {
        store: this.rootStore.phaseStore,
        modelId: 'phaseId',
      },
    ];
  }

  async deactivate(id, remark) {
    await this.api.deactivate(id, remark)
      .then(() => {
        this.load(id);
        this.rootStore.inspectionTaskResultStore.loadAll({params: {inspectionTaskId: id}});
        this.rootStore.inspectionTaskPendingStore.pendingInspectionTasks = this.rootStore
          .inspectionTaskPendingStore.pendingInspectionTasks.filter((pit) => pit.inspectionTaskId !== id);
        this.addMessage({type: 'success', title: 'flashMessages.deactivateSuccess'},
          {skipNotification: false});
      }).catch((e) => this.handleApiError(e, 'deactivate'));
  }

  async activate(id) {
    await this.api.activate(id)
      .then(() => {
        this.load(id);
        this.rootStore.inspectionTaskPendingStore.loadAll({params: {inspectionTaskId: id}});
        this.addMessage({type: 'success', title: 'flashMessages.reactivateSuccess'},
          {skipNotification: false});
      }).catch((e) => this.handleApiError(e, 'activate'));
  }

  getSameGroup(inspectionTask, pendingOnly = false) {
    if (inspectionTask.inspectionTaskGroupId) {
      const pendingInspectionTaskIds = this.rootStore.inspectionTaskPendingStore.pendingInspectionTasks
        .map((pending) => pending.inspectionTaskId);
      return this.inspectionTasks.filter((current) =>
        current.inspectionTaskGroupId === inspectionTask.inspectionTaskGroupId
        && current.workplaceId === inspectionTask.workplaceId
        && current.phaseId === inspectionTask.phaseId)
        .filter((current) => ((pendingOnly) ? pendingInspectionTaskIds.includes(current.id) : true))
        .sort((a, b) => {
          if (a.characteristicNo !== b.characteristicNo) {
            return sortAlphabetically(a.characteristicNo, b.characteristicNo);
          }
          return a.id - b.id;
        });
    }
    return [inspectionTask];
  }

  getByInspectionLotId(inspectionLotId) {
    return this.inspectionTasks.filter((task) => task.inspectionLotId === inspectionLotId);
  }

  getByOrder(orderId) {
    return this.inspectionTasks.filter((task) => task.inspectionLot?.orderId === orderId);
  }

  getTemplates() {
    return this.inspectionTasks.filter((task) => task.isTemplateTask === true);
  }

  getValidByWorkplaceIdAndOrderId(workplaceId, orderId) {
    return this.inspectionTasks.filter((task) => {
      if (task.workplaceId !== workplaceId) {
        if (task.showOnSiblingVirtualWorkplaces === false) {
          return false;
        }
        const siblingVirtualWorkplaces = this.rootStore.workplaceStore.getVirtualSiblingWorkplaces(workplaceId);

        if (!siblingVirtualWorkplaces.map((w) => w.id).includes(task.workplaceId)) {
          return false;
        }
      }
      if (orderId !== null) {
        return task.inspectionLot?.orderId === orderId;
      }
      if (task.inspectionLot?.orderId) {
        return false;
      }
      return task.inspectionLot ? moment().isBetween(
        moment(task.inspectionLot.validStart),
        moment(task.inspectionLot.validEnd),
        null,
        '[]'
      ) : false;
    });
  }

  updateTargetValues({params, apiEndpoint, target}) {
    const entity = this.getById(params.id);
    const query = this.loadAll({
      apiEndpoint,
      params,
      raw: true,
    });
    query.then(({data}) => {
      entity[target] = {
        ...entity[target],
        ...data,
      };
      this.inspectionTasks = [...this.inspectionTasks];
    });
    return query;
  }

  updateQuantitativeTargetValues(params) {
    return this.updateTargetValues({
      params,
      apiEndpoint: updateQuantitativeTargetValuesEndpoint,
      target: 'quantitative',
    });
  }

  updateQualitativeTargetValues(params) {
    return this.updateTargetValues({
      params,
      apiEndpoint: updateQualitativeTargetValuesEndpoint,
      target: 'qualitative',
    });
  }

  async search({params}) {
    return this.api.search(params).then((response) => response.data);
  }
}

decorate(InspectionTaskStore, {
  inspectionTasks: observable,
  getByOrderId: observable,
  updateQuantitativeTargetValues: action,
  updateQualitativeTargetValues: action,
  updateTargetValues: action,
});
